import { ItemsPerPage, Table } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import { Row } from 'react-table'

import { fetchCountries } from '@modules-deprecated/app/countries/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useExpenseRevampFeatureFlags } from '@views/bills/hooks/useExpenseRevampFeatureFlags'

import { EmberRoute } from '../../../../../../enums/emberRoute'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { SortDirection } from '../../../../../../enums/sortDirection'
import { TableIds } from '../../../../../../enums/tableIds'
import { useOrganizationSource, useSegment } from '../../../../../../hooks'
import { useEmberRouteToUrlGenerator } from '../../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { getCurrentLocale } from '../../../../../../utils'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useContacts } from '../../../../hooks/useContacts'
import { useContactsListFilters } from '../../../../hooks/useContactsListFilters'
import { ContactsSortProperty } from '../../../../query-api'
import { useContactType } from '../../contexts/contactTypeContext'
import { ContactsTableRow, useContactsTableData } from './hooks/useContactsTableData'
import { getTableColumns } from './utils/getTableColumns'

export const ContactsTable = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { generateUrl } = useEmberRouteToUrlGenerator()

  const locale = getCurrentLocale()
  const { data } = useQuery([QueryKeys.Countries, locale], () => fetchCountries(locale))

  const { isCustomer, isSupplier } = useContactType()
  const { contacts, isLoading, pagination } = useContacts({ isCustomer, isSupplier })
  const [{ sortDirection, sortProperty, pageSize }, setFilters] = useContactsListFilters()
  const { isSourceBilly } = useOrganizationSource()

  const withStaticActions = isCustomer && !isSourceBilly
  const tableData = useContactsTableData(contacts, t, withStaticActions, data?.countries)
  const columns = getTableColumns(t, withStaticActions)

  const sortedColumnId = useMemo(() => {
    return getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc)
  }, [sortDirection, sortProperty])

  const handlePageChange = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setFilters({ page: 1, pageSize: itemsPerPage })
    },
    [setFilters],
  )

  const { isRevampedPurchaseListEnabled } = useExpenseRevampFeatureFlags()

  const handleRowClick = useCallback(
    (row: Row<ContactsTableRow>) => {
      if (isCustomer) {
        track('XXX - Invoicing Success - Customers List - Table row clicked')
      }

      if (isSupplier && !isRevampedPurchaseListEnabled) {
        if (generateUrl) {
          const url = generateUrl(EmberRoute.ContactsSupplier, row.original.id)
          navigate(url)
        }

        return
      }

      if (isSupplier && isRevampedPurchaseListEnabled) {
        navigate(
          getOrganizationViewUrl(
            organization?.url,
            reactRoute.organization.suppliers.getSupplierDetailRoute(row.original.id),
          ),
        )

        return
      }

      if (isSourceBilly) {
        if (generateUrl) {
          const url = generateUrl(EmberRoute.ContactsCustomer, row.original.id)
          navigate(url)
        }

        return
      }

      navigate(
        getOrganizationViewUrl(organization?.url, reactRoute.organization.clients.getViewClientRoute(row.original.id)),
      )
    },
    [
      generateUrl,
      isCustomer,
      isRevampedPurchaseListEnabled,
      isSourceBilly,
      isSupplier,
      navigate,
      organization?.url,
      track,
    ],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setFilters({
        page: 1,
        sortProperty: columnId as ContactsSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setFilters],
  )

  return (
    <Table
      id={TableIds.ContactsTable}
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      currentPage={pagination?.page}
      totalPageItems={pagination?.total}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      sortedColumnId={sortedColumnId}
      onSort={handleSort}
      withSkeletonView
      withColumnsFiltering
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
